import { create } from 'zustand';
import {
  fetchConversationsListParams,
  SidebarItem,
} from '../../../components/blocks/layout/types/sidebar-conversation';
import { Attachment, Message } from '../../../features/chat/types/chat';
import { useAuthStore } from '../auth/useAuthStore';
import { v4 as uuidv4 } from 'uuid';
import { clients } from '../../../lib/https';
import { useModelStore } from './use-model-store';
import { WebsocketResponseTypes } from '../../../models/websockets.types';
import { WebsocketServices } from '../../../services/websocket-services';
import { ButtonIds } from '../../../pages/chat/chat';
import { getRefreshToken } from 'features/auth/services/auth-service';
import { ConfigurationSettings } from '/models/configuration-settings';

const extractLastResponseContent = (content: string) => {
  const lastIndefOfResponseOpenTag = content.lastIndexOf('<response>');
  if (lastIndefOfResponseOpenTag !== -1) {
    const lastIndefOfResponseCloseTag = content.indexOf('</response>', lastIndefOfResponseOpenTag);
    if (lastIndefOfResponseCloseTag !== -1) {
      return content.slice(
        lastIndefOfResponseOpenTag + '<response>'.length,
        lastIndefOfResponseCloseTag
      );
    } else {
      return content.slice(lastIndefOfResponseOpenTag + '<response>'.length);
    }
  }
  return content;
};

export interface ConversationState {
  id: string;
  title: string;
  messages: Message[];
  isLoading: boolean;
  isCurrentMessageLoading: boolean;
  isCurrentResponseGenerating: boolean;
  hasFetchedInitialMessages: boolean;
  error?: string | null;
  modelName?: string;
  allFileIds?: string[];
}

export interface ChatStoreState {
  lastSearchStatus: boolean;
  itemIdFilePathMap: Map<string, string>;
  activeConversationId: string | null;
  streamingAssistantMessageId: string | null;
  conversations: Record<string, ConversationState>;
  webSocket: WebSocket | null;
  webSocketStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
  sidebarConversationsList: SidebarItem[];
  searchConversationsList: SidebarItem[];
  isSidebarLoading: boolean;
  sidebarError: string | null;
  reconnectAttempts: number;
  maxReconnectAttempts: number;
  reconnectTimeout: number;
  reconnect: (config: ConfigurationSettings) => void;
  initializeWebSocket: (config: ConfigurationSettings) => void;
  closeWebSocket: () => void;
  _handleWebSocketMessage: (event: MessageEvent) => void;
  _addMessageToActiveConversation: (msg: Message, assistantMessageIdToUpdate?: string) => void;
  sendMessage: (
    content: string,
    config: ConfigurationSettings,
    newConversationId?: string,
    attachments?: Attachment[],
    isFeatureSelected?: Record<ButtonIds, boolean>
  ) => Promise<void>;
  setActiveConversationId: (conversationId: string | null) => void;
  setActiveConversation: (
    conversationId: string | null,
    config: ConfigurationSettings
  ) => Promise<void>;
  fetchAndSetConversationMessages: (
    conversationId: string,
    config: ConfigurationSettings
  ) => Promise<void>;
  updateConversationTitleInStore: (conversationId: string, newTitle: string) => void;
  appendToSidebarConversationsList: (newItem: SidebarItem) => void;
  _sortSidebarConversations: (newList: SidebarItem[]) => SidebarItem[];
  _moveConversationToToday: (conversationId: string) => void;
  fetchSidebarConversations: (
    config: ConfigurationSettings,
    params?: fetchConversationsListParams
  ) => Promise<void>;
  removeConversationFromSidebarById: (conversationId: string) => void;
  fetchSearchResults: (search: string, config: ConfigurationSettings) => Promise<void>;
  setItemIdFilePathMap: (key: string, value: string) => void;
  _wsGuard: (config: ConfigurationSettings) => void;
}

export const initialConversationState = (id: string, title = 'New Chat'): ConversationState => ({
  id,
  title,
  messages: [],
  isLoading: false,
  isCurrentMessageLoading: false,
  isCurrentResponseGenerating: false,
  hasFetchedInitialMessages: false,
  error: null,
  allFileIds: [],
});

export const useChatStore = create<ChatStoreState>((set, get) => ({
  lastSearchStatus: false,
  activeConversationId: null,
  streamingAssistantMessageId: null,
  conversations: {},
  webSocket: null,
  webSocketStatus: 'disconnected',
  sidebarConversationsList: [],
  searchConversationsList: [],
  isSidebarLoading: false,
  sidebarError: null,
  reconnectAttempts: 0,
  maxReconnectAttempts: 5,
  reconnectTimeout: 1000,
  itemIdFilePathMap: new Map<string, string>(),

  setItemIdFilePathMap: (key: string, value: string) => {
    set((state) => ({
      itemIdFilePathMap: state.itemIdFilePathMap.set(key, value),
    }));
  },

  initializeWebSocket: (config: ConfigurationSettings) => {
    const accessToken = useAuthStore.getState().accessToken;
    if (
      !accessToken ||
      get().webSocketStatus === 'connected' ||
      get().webSocketStatus === 'connecting'
    )
      return;

    const wsUrl = `wss://${config.paths.taskforceBaseUrl}${config.paths.taskforce.chatAll}`;
    if (!wsUrl) {
      console.error('WebSocket URL is not configured.');
      set({ webSocketStatus: 'error' });
      return;
    }

    set({ webSocketStatus: 'connecting' });
    const ws = new WebSocket(wsUrl, accessToken);

    ws.onopen = () => {
      set({
        webSocket: ws,
        webSocketStatus: 'connected',
        reconnectAttempts: 0,
        reconnectTimeout: 1000,
      });
    };

    ws.onmessage = (event) => {
      get()._handleWebSocketMessage(event);
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      set({ webSocketStatus: 'error', webSocket: null });
      get().reconnect(config);
    };

    ws.onclose = () => {
      set({ webSocketStatus: 'disconnected', webSocket: null });
      get().reconnect(config);
    };
  },

  _wsGuard: async (config: ConfigurationSettings) => {
    try {
      const accessToken = await getRefreshToken(config);
      useAuthStore.getState().setAccessToken(accessToken);
    } catch (error) {
      useAuthStore.getState().logout();
      console.error('WebSocket guard failed, user logged out:', error);
    }
  },

  reconnect: (config: ConfigurationSettings) => {
    get()._wsGuard(config);
    const { reconnectAttempts, maxReconnectAttempts, reconnectTimeout } = get();
    const accessToken = useAuthStore.getState().accessToken;

    if (reconnectAttempts >= maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    setTimeout(() => {
      if (accessToken) {
        get().initializeWebSocket(config);
        set((state) => ({
          reconnectAttempts: state.reconnectAttempts + 1,
          reconnectTimeout: Math.min(state.reconnectTimeout * 2, 30000),
        }));
      }
    }, reconnectTimeout);
  },

  closeWebSocket: () => {
    get().webSocket?.close();
    set({ webSocket: null, webSocketStatus: 'disconnected' });
  },

  _addMessageToActiveConversation: (message, assistantMessageIdToUpdate) => {
    const { activeConversationId } = get();
    if (!activeConversationId) return;

    set((state) => {
      const currentConv = state.conversations[activeConversationId];
      if (!currentConv) return {};

      const newMessages = [...currentConv.messages];
      if (message.role === 'assistant' && assistantMessageIdToUpdate) {
        const existingIndex = newMessages.findIndex((m) => m.id === assistantMessageIdToUpdate);
        if (existingIndex !== -1) {
          newMessages[existingIndex] = message;
        } else {
          newMessages.push(message);
        }
      } else {
        newMessages.push(message);
      }

      return {
        conversations: {
          ...state.conversations,
          [activeConversationId]: {
            ...currentConv,
            messages: newMessages,
          },
        },
      };
    });
  },

  updateConversationTitleInStore: (conversationId, newTitle) => {
    set((state) => {
      const updatedConversations = state.conversations[conversationId]
        ? {
            ...state.conversations,
            [conversationId]: {
              ...state.conversations[conversationId],
              title: newTitle,
            },
          }
        : state.conversations;

      const updatedSidebarList = state.sidebarConversationsList.map((item) =>
        item.ConversationId === conversationId
          ? {
              ...item,
              ConversationTitle: newTitle,
            }
          : item
      );

      return {
        conversations: updatedConversations,
        sidebarConversationsList: updatedSidebarList,
      };
    });
  },

  _handleWebSocketMessage: (event: MessageEvent) => {
    const { activeConversationId, streamingAssistantMessageId, conversations, lastSearchStatus } =
      get();
    const selectedModel = useModelStore.getState().selectedModel;

    if (!activeConversationId || !conversations[activeConversationId]) {
      console.warn('Received WS message but no active conversation or streaming ID.');
      return;
    }
    const currentConv = conversations[activeConversationId];
    const websocketResponse = WebsocketServices.websocketResponseParser(event.data);

    switch (websocketResponse.type) {
      case WebsocketResponseTypes.END_OF_STREAM: {
        set((state) => ({
          conversations: {
            ...state.conversations,
            [activeConversationId]: {
              ...currentConv,
              isCurrentMessageLoading: false,
              isCurrentResponseGenerating: false,
            },
          },
        }));
        break;
      }
      case WebsocketResponseTypes.SERVER_ERROR:
        set((state) => ({
          conversations: {
            ...state.conversations,
            [activeConversationId]: {
              ...currentConv,
              isCurrentMessageLoading: false,
              isCurrentResponseGenerating: false,
              error: websocketResponse.data as string,
            },
          },
        }));
        break;
      case WebsocketResponseTypes.CONVERSATION_TITLE:
        get().updateConversationTitleInStore(
          activeConversationId,
          websocketResponse.data as string
        );
        break;
      case WebsocketResponseTypes.LLM_ANSWER: {
        if (!streamingAssistantMessageId) {
          console.warn('Received WS data chunk, but no streamingAssistantMessageId is set.');
          return;
        }

        const assistantMsgIndex = currentConv.messages.findIndex(
          (msg) => msg.id === streamingAssistantMessageId && msg.role === 'assistant'
        );

        const isThinkingModel = selectedModel?.isThinkingModel;

        let obj: { thinkContent?: string; content: string };
        if (isThinkingModel && !lastSearchStatus) {
          const responseContents = (websocketResponse.data as string).split('</think>');
          obj = {
            thinkContent: responseContents[0],
            content: responseContents.length > 1 ? responseContents[1] : '',
          };
        } else {
          obj = {
            content: websocketResponse.data as string,
          };
        }

        obj.content = extractLastResponseContent(obj.content);

        obj.content = obj.content.replace(/\n\n/g, '\n').replace(/\\n/g, '\n');

        if (assistantMsgIndex === -1) {
          const newAssistantMessage: Message = {
            id: streamingAssistantMessageId,
            role: 'assistant',
            ...obj,
            createdDate: Date.now(),
          };
          get()._addMessageToActiveConversation(newAssistantMessage, streamingAssistantMessageId);
        } else {
          const updatedMessages = [...currentConv.messages];
          const existingMsg = updatedMessages[assistantMsgIndex];
          updatedMessages[assistantMsgIndex] = {
            ...existingMsg,
            ...obj,
          };
          set((state) => ({
            conversations: {
              ...state.conversations,
              [activeConversationId]: {
                ...currentConv,
                messages: updatedMessages,
                isCurrentMessageLoading: false,
              },
            },
          }));
        }
        break;
      }
    }
  },

  sendMessage: async (
    content: string,
    config: ConfigurationSettings,
    newConversationId?: string,
    attachments?: Attachment[],
    isFeatureSelected?: Record<ButtonIds, boolean>
  ) => {
    const { activeConversationId, webSocket, webSocketStatus } = get();
    const accessToken = useAuthStore.getState().accessToken;
    const selectedModel = useModelStore.getState().selectedModel;
    const newFileIds = attachments?.map((item) => item.fileId) ?? [];
    let updatedAllFileIds = newFileIds;
    set({ lastSearchStatus: isFeatureSelected?.[ButtonIds.SEARCH] ?? false });

    if (!webSocket || webSocketStatus !== 'connected') {
      if (webSocketStatus !== 'connecting') {
        if (accessToken) get().initializeWebSocket(config);
      }
      return;
    }

    const userMessage: Message = {
      id: uuidv4(),
      role: 'user',
      content,
      createdDate: Date.now(),
      attachments: attachments,
    };

    let conversationIdToUse = activeConversationId;

    if (!conversationIdToUse) {
      conversationIdToUse = newConversationId ?? uuidv4();
      const newConvState = initialConversationState(conversationIdToUse, 'New Chat'); // Temporary title
      newConvState.messages = [userMessage];
      newConvState.isCurrentMessageLoading = true;
      newConvState.isCurrentResponseGenerating = true;
      newConvState.modelName = selectedModel?.id;
      newConvState.allFileIds = newFileIds;

      set((state) => ({
        activeConversationId: conversationIdToUse,
        conversations: {
          ...state.conversations,
          [conversationIdToUse!]: newConvState,
        },
      }));

      const isoDate = new Date().toISOString();
      const newSidebarEntry: SidebarItem = {
        ConversationId: conversationIdToUse!,
        ConversationTitle: 'New Chat', // Temporary title, will be updated by _handleWebSocketMessage or _moveConversationToToday
        Conversations: [], // Assuming this is for potential sub-items, adjust if not used
        CreatedBy: '', // Populate if available from authStore or user profile
        CreatedDate: isoDate,
        LastUpdatedDate: isoDate,
        TenantId: '', // Populate if available
      };
      get().appendToSidebarConversationsList(newSidebarEntry);
    } else {
      get()._addMessageToActiveConversation(userMessage);

      if (conversationIdToUse) {
        get()._moveConversationToToday(conversationIdToUse);
      }

      const currentConv = get().conversations[conversationIdToUse!];
      const prevAllFileIds = currentConv.allFileIds ?? [];
      updatedAllFileIds = [...prevAllFileIds, ...newFileIds];

      set((state) => ({
        conversations: {
          ...state.conversations,
          [conversationIdToUse!]: {
            ...state.conversations[conversationIdToUse!],
            isCurrentMessageLoading: true,
            isCurrentResponseGenerating: true,
            modelName: selectedModel?.id,
            allFileIds: updatedAllFileIds,
          },
        },
      }));
    }

    const assistantMessageId = uuidv4();
    set({ streamingAssistantMessageId: assistantMessageId });

    get().webSocket?.send(
      JSON.stringify({
        question: userMessage.content,
        conversation: [],
        file_ids: isFeatureSelected?.[ButtonIds.SEARCH] ? [] : updatedAllFileIds,
        filter_key_value_pair: [],
        model_name: selectedModel?.id,
        is_regenerate: false,
        conversation_id: conversationIdToUse,
        curr_file_ids: userMessage.attachments?.map((file) => file.fileId),
        search_online: isFeatureSelected?.[ButtonIds.SEARCH] ?? false,
      })
    );
  },

  setActiveConversationId: (conversationId) => {
    set({
      activeConversationId: conversationId,
    });
  },
  setActiveConversation: async (conversationId, config: ConfigurationSettings) => {
    set({ activeConversationId: conversationId });
    if (conversationId) {
      const existingConv = get().conversations[conversationId];
      if (!existingConv) {
        // First time seeing this ID, create a placeholder and fetch
        set((state) => ({
          conversations: {
            ...state.conversations,
            [conversationId]: initialConversationState(conversationId, 'Loading...'),
          },
        }));
        await get().fetchAndSetConversationMessages(conversationId, config);
      } else if (!existingConv.hasFetchedInitialMessages && !existingConv.messages.length) {
        // Exists but never fetched (e.g. from sidebar click before data loaded for it)
        await get().fetchAndSetConversationMessages(conversationId, config);
      }
    }
  },

  fetchAndSetConversationMessages: async (conversationId, config: ConfigurationSettings) => {
    if (!conversationId) return;
    let modelName: string,
      isThinkingModel: boolean,
      responseContentObject: { thinkContent?: string; content: string };

    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversationId]: {
          ...(state.conversations[conversationId] || initialConversationState(conversationId)),
          isLoading: true, // Loading history
          error: null,
        },
      },
    }));

    try {
      const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
      const url = `${baseUrl}${config.paths.taskforce.singleConversation}/${conversationId}`;
      const res = await clients.get<{ result: any }>(url, config.blocksKey);

      const fetchedMessages: Message[] = [];
      let conversationTitle = 'Chat'; // Default title
      const allFileIds: string[] = [];

      if (res && res.result) {
        conversationTitle =
          res.result.ConversationTitle || `Chat ${conversationId.substring(0, 5)}`;
        modelName = res.result.ModelName;
        isThinkingModel = useModelStore
          .getState()
          .models.some((model) => model.id === modelName && model.isThinkingModel);

        if (res.result.Conversations && res.result.Conversations.length > 0) {
          for (const conversationItem of res.result.Conversations) {
            // Assuming this is the array of Q&A pairs
            if (conversationItem.QADetails && conversationItem.QADetails.length > 0) {
              conversationItem.QADetails.forEach((qa: any) => {
                const userFiles: Attachment[] = [];
                const assistantFiles: Attachment[] = [];
                qa?.Files?.forEach((file: any) => {
                  if (file.Sender === 'user') {
                    userFiles.push({
                      fileId: file.FileId,
                      fileName: file.FileName,
                      extension: file.Extension,
                      sender: 'user',
                    });
                    allFileIds.push(file.FileId);
                  } else {
                    assistantFiles.push({
                      fileId: file.FileId,
                      fileName: file.FileName,
                      extension: file.Extension,
                      sender: 'assistant',
                    });
                  }
                });

                if (isThinkingModel) {
                  const splitsContents = qa.Answer.split('</think>');
                  responseContentObject = {
                    thinkContent: splitsContents.length > 1 ? splitsContents[0] : '',
                    content: splitsContents.length > 1 ? splitsContents[1] : splitsContents[0],
                  };
                } else {
                  responseContentObject = {
                    content: qa.Answer,
                  };
                }

                responseContentObject.content = extractLastResponseContent(
                  responseContentObject.content
                );
                responseContentObject.content = responseContentObject.content
                  .replace(/\n\n/g, '\n')
                  .replace(/\\n/g, '\n');

                fetchedMessages.push({
                  id: uuidv4(), // Consider if API provides stable IDs
                  role: 'user',
                  content: qa.Question,
                  createdDate: new Date(qa.AskedOn || Date.now()).getTime(),
                  attachments: userFiles,
                });
                fetchedMessages.push({
                  id: conversationItem.RecordId, // Consider if API provides stable IDs
                  role: 'assistant',
                  ...responseContentObject,
                  createdDate: new Date(qa.AnsweredOn || Date.now()).getTime(),
                  attachments: assistantFiles,
                  feedback: qa?.Feedback ?? undefined,
                });
              });
            }
          }
        }
      }

      set((state) => ({
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...(state.conversations[conversationId] || initialConversationState(conversationId)),
            messages: fetchedMessages,
            title: conversationTitle,
            isLoading: false, // Done loading history
            hasFetchedInitialMessages: true,
            error: null,
            modelName: modelName,
            allFileIds: allFileIds,
          },
        },
      }));
    } catch (error) {
      console.error('Failed to fetch conversation:', error);
      set((state) => ({
        conversations: {
          ...state.conversations,
          [conversationId]: {
            ...(state.conversations[conversationId] || initialConversationState(conversationId)),
            isLoading: false,
            hasFetchedInitialMessages: false, // So it can be retried
            error: 'Failed to load messages.',
          },
        },
      }));
    }
  },

  _sortSidebarConversations: (list: SidebarItem[]): SidebarItem[] => {
    return [...list].sort(
      (a, b) => new Date(b.LastUpdatedDate).getTime() - new Date(a.LastUpdatedDate).getTime()
    );
  },

  fetchSidebarConversations: async (
    config: ConfigurationSettings,
    params = {
      page: 0,
      pageSize: 20,
    }
  ) => {
    set({ isSidebarLoading: true, sidebarError: null });
    try {
      const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
      const url = `${baseUrl}${config.paths.taskforce.conversationList}`;
      const res = await clients.get<{ results: SidebarItem[] }>(
        `${url}?page=${params.page}&page_size=${params.pageSize}`,
        config.blocksKey
      );

      if (res && res.results) {
        const sortedResults = get()._sortSidebarConversations(res.results);
        set({
          sidebarConversationsList: sortedResults,
          searchConversationsList: sortedResults,
          isSidebarLoading: false,
        });
      } else {
        throw new Error('Invalid response structure from conversations-list API');
      }
    } catch (error) {
      console.error('Failed to fetch sidebar conversations:', error);
      set({
        isSidebarLoading: false,
        sidebarError: error instanceof Error ? error.message : 'Unknown error fetching sidebar',
      });
    }
  },

  removeConversationFromSidebarById: (conversationId: string) => {
    set((state) => ({
      sidebarConversationsList: state.sidebarConversationsList.filter(
        (item) => item.ConversationId !== conversationId
      ),
    }));
  },

  appendToSidebarConversationsList: (newItem: SidebarItem) => {
    set((state) => {
      // Prevent duplicates if the item somehow already exists
      if (
        state.sidebarConversationsList.find((it) => it.ConversationId === newItem.ConversationId)
      ) {
        // If it exists, we might want to call _moveConversationToToday instead,
        // but for a true "append new", it shouldn't exist.
        // console.warn(`appendToSidebarConversationsList: Item ${newItem.ConversationId} already exists. Moving to top.`);
        // For now, let's ensure it's at the top if it exists, or add it if it doesn't.
        // This might be redundant if _moveConversationToToday is called.
        // Let's simplify: if called, assume it's new and should be at the top.
        const filteredList = state.sidebarConversationsList.filter(
          (it) => it.ConversationId !== newItem.ConversationId
        );
        return { sidebarConversationsList: [newItem, ...filteredList] }; // newItem is newest
      }
      // newItem is the newest, so prepend it. The list remains sorted.
      return { sidebarConversationsList: [newItem, ...state.sidebarConversationsList] };
    });
  },

  _moveConversationToToday: (conversationId: string) => {
    set((state) => {
      const itemIndex = state.sidebarConversationsList.findIndex(
        (it) => it.ConversationId === conversationId
      );

      if (itemIndex === -1) {
        // console.warn(`_moveConversationToToday: Item ${conversationId} not found in sidebar. It might be a new chat handled by append.`);
        // If item is not in sidebar, it might be a brand new chat not yet fetched,
        // or an edge case. sendMessage should handle adding new items via appendToSidebarConversationsList.
        return {};
      }

      const itemToMove = { ...state.sidebarConversationsList[itemIndex] };
      itemToMove.LastUpdatedDate = new Date().toISOString();

      // Update title from main conversation state if available and more specific
      const conversationState = state.conversations[conversationId];
      if (
        conversationState &&
        conversationState.title &&
        conversationState.title !== 'New Chat' &&
        conversationState.title !== 'Loading...'
      ) {
        itemToMove.ConversationTitle = conversationState.title;
      }

      const newListWithoutItem = [
        ...state.sidebarConversationsList.slice(0, itemIndex),
        ...state.sidebarConversationsList.slice(itemIndex + 1),
      ];

      // Add the updated item to the beginning, making it the most recent.
      return { sidebarConversationsList: [itemToMove, ...newListWithoutItem] };
    });
  },

  fetchSearchResults: async (search: string, config: ConfigurationSettings) => {
    try {
      const baseUrl = `https://${config.paths.taskforceBaseUrl}`;
      const url = `${baseUrl}${config.paths.taskforce.conversationList}`;
      const res = await clients.get<{ results: SidebarItem[] }>(
        `${url}?page=${0}&page_size=${200}&search=${search}`,
        config.blocksKey
      );

      if (res && res.results) {
        const sortedResults = get()._sortSidebarConversations(res.results);
        set({
          searchConversationsList: sortedResults,
        });
      } else {
        throw new Error('Invalid response structure from conversations-list API');
      }
    } catch (error) {
      console.error('Failed to fetch search results:', error);
    }
  },
}));
